import {Box, Card, CardContent, Chip, Divider, Typography, Paper} from "@mui/material";
import React from "react";
import {RiskReportTypes} from "../../types";
import {
    PersonSearch
} from '@mui/icons-material';
import palette from "../../theme/palette";
import {
    OffenderPhoto,
    OffenderField,
    formatPhysical,
    formatDemographics,
    offenderTileSx,
    offenderChipSx,
    tileDividerSx,
    chargesBlockSx
} from "./offender-card-parts";

interface Props {
    assessmentData : RiskReportTypes.RiskResponse;
}

const isRegistryFlagSet = (value?: string) => {
    if (!value) return false;
    return !['false', 'no', 'n', '0', ''].includes(String(value).trim().toLowerCase());
};

// Section 2 of the redesigned report: the registry match for the person being
// screened, kept separate from other offenders at the address (section 3).
export const SubjectOffenderMatch: React.FC<Props> = ({ assessmentData }) => {

    // Sparse reports (no offender block) reach this panel — see news-articles.tsx.
    const { nameMatchesInSexOffender } = assessmentData ?? {};
    const matchingRecords = nameMatchesInSexOffender?.matchingRecords ?? [];

    let matchTagText = "POSSIBLE NAME MATCH FOUND";
    if (nameMatchesInSexOffender?.possibleMatchFound && (nameMatchesInSexOffender?.addressMatched || nameMatchesInSexOffender?.birthDateMatch)) {
        matchTagText = "MATCH FOUND";
    }

    // The match-quality flags are aggregate on nameMatchesInSexOffender (the
    // API does not set them per record), so every card carries the same badge
    // by design. Computed once, outside the record map.
    const recordBadge = () => {
        if (nameMatchesInSexOffender?.addressMatched && nameMatchesInSexOffender?.birthDateMatch) return "DIRECT MATCH";
        if (nameMatchesInSexOffender?.addressMatched) return "ADDRESS MATCH";
        if (nameMatchesInSexOffender?.birthDateMatch) return "DOB MATCH";
        return "POSSIBLE NAME MATCH";
    };
    const recordBadgeLabel = recordBadge();

    return (
        <Card sx={{ mb: 3 }}>
            <CardContent sx={{ p: 4 }}>
                <Box className={'pdf-keep-with-next'} sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', bgcolor: palette.darkBlue, p: 1, borderRadius: 1, mr: 2 }}>
                        <PersonSearch sx={{ color: 'white' }} />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" fontWeight={500}>
                            Subject Sex Offender Match
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Registry match for the person being screened
                        </Typography>
                    </Box>
                    {nameMatchesInSexOffender?.possibleMatchFound && matchingRecords.length > 0 && (
                        <Chip
                            label={matchTagText}
                            variant="outlined"
                            size="small"
                            sx={{
                                mr: 1,
                                bgcolor: '#eb32231a',
                                color: '#EB3223'
                            }}
                        />
                    )}
                </Box>

                {matchingRecords.length > 0 ? (
                    matchingRecords.map((offender: RiskReportTypes.SexOffender, index: number) => (
                        <Paper
                            key={index}
                            elevation={0}
                            className={'pdf-avoid-break'}
                            sx={{ ...offenderTileSx, p: 3, m: 1 }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                                <OffenderPhoto offender={offender} size={72} />
                                <Box sx={{ flex: 1, minWidth: 0, pt: 0.5 }}>
                                    <Typography
                                        fontWeight={600}
                                        sx={{
                                            fontSize: 18,
                                            color: '#212B36',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                        }}
                                        className={'text-download-adjust'}
                                    >
                                        {offender.sex_offender_name || 'N/A'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                        {formatDemographics(offender)}
                                    </Typography>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                                    <Chip label={recordBadgeLabel} size="small" sx={offenderChipSx} />
                                    {isRegistryFlagSet(offender.sex_offender_is_predator) && (
                                        <Chip label={"PREDATOR"} size="small" sx={offenderChipSx} />
                                    )}
                                    {isRegistryFlagSet(offender.sex_offender_is_absconder) && (
                                        <Chip label={"ABSCONDER"} size="small" sx={offenderChipSx} />
                                    )}
                                </Box>
                            </Box>

                            <Divider sx={tileDividerSx} />

                            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' }, gap: 1.5 }}>
                                <OffenderField
                                    label="ADDRESS"
                                    value={[offender.sex_offender_address_line1, offender.sex_offender_address_line2].filter(Boolean).join(', ')}
                                />
                                <OffenderField label="DOB/YOB" value={offender.sex_offender_birthdate} />
                                <OffenderField label="PHYSICAL" value={formatPhysical(offender)} />
                                {!!offender.sex_offender_marks && (
                                    <OffenderField label="MARKS" value={offender.sex_offender_marks} />
                                )}
                                {!!offender.sex_offender_aliases && (
                                    <OffenderField label="ALIASES" value={offender.sex_offender_aliases} />
                                )}
                                <OffenderField
                                    label="CHARGES"
                                    value={offender.sex_offender_charges}
                                    sx={{ ...chargesBlockSx, gridColumn: '1 / -1' }}
                                />
                            </Box>
                        </Paper>
                    ))
                ) : (
                    <Paper sx={{ p: 3, border: '1px solid #e0e0e0', textAlign: 'center', width: '100%' }}>
                        <Typography variant="body1" color="info" sx={{ mb: 1 }}>
                            No Registry Match Found for the Subject
                        </Typography>
                    </Paper>
                )}
            </CardContent>
        </Card>
    )
}
